import React, { useState } from 'react'
import { IQuestionViewProps } from '../types'
import { StarRatingQuestionModelTypes } from '@aytm/app/swidget/base/questions/StarRating/types'

import useViewport from '../../modules/viewport/useViewport'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useSession from '../../session/useSession'
import usePreviewOptions from '../../modules/preview/usePreviewOptions'

import Stars from './stars/Stars'
import ImageAsset from '../../modules/images/ImageAsset'
import TextWithOptions from '../../modules/preview/components/TextWithOptions'
import { hapticEffects } from '../../lib/hapticEffects'

import style from './style.module.scss'
import commonStyle from '../../questions/_common/style.module.scss'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'

const StarRatingView = ({
  schema,
  response,
  onUpdateResponse,
  showValidation,
  responseValidation
}: IQuestionViewProps<StarRatingQuestionModelTypes>) => {
  const viewport = useViewport()
  const session = useSession()
  const previewOptions = usePreviewOptions()
  const { t } = useSurveyLocale()
  const { localeOptions } = useInterfaceLocale()
  const [focused, setFocused] = useState<number | null>(null)

  const hasSmallImages = schema.matrix_batches.filter(b => !b.hidden).some(batch => batch.image && !batch.image.expand)
  const disabled = false == session.rules?.show?.[schema.id] && !session.skipValidation

  const onChangeResponse = (batchIndex: number, value: number) => {
    response.data[batchIndex].value = value
    hapticEffects.selection()
    onUpdateResponse(response)
  }

  const naMode = schema.matrix_batches[0]?.answers?.some(a => a.na)
  const starRatingApproximateWidth = 28 * schema.degree + (naMode ? 40 : 0)
  const compactStarsLayout = hasSmallImages && viewport.width - starRatingApproximateWidth < 120
  const imageAssetWidth = viewport.mobile || (viewport.emulated && viewport.width <= 500) ? 36 : 66
  return (
    <fieldset
      className={`${style.StarRatingView} ${localeOptions.rtl ? style.StarRatingViewRTL : ''} ${
        viewport.mobile || (viewport.emulated && viewport.width <= 500) ? style.mobile : ''
      } ${disabled ? commonStyle.disabled : ''}`}
      data-testid="SW-SR"
    >
      <legend className={commonStyle.srOnly}>{schema.text}</legend>
      {!schema.matrix_batches.filter(b => !b.hidden).length ? (
        <div data-testid="SW-SR-NoAnswers">{t('questions', 'noAnswers')}</div>
      ) : null}

      {schema.matrix_batches.map((batch, index) => (
        <div
          className={`
            ${commonStyle.answerBlock} ${style.answerBlock}
            ${compactStarsLayout ? style.compact : ''}
            ${index === focused ? style.Focused : ''}
            ${showValidation && responseValidation.invalidBatches?.includes(batch.id) ? style.error : ''}
            ${batch.hidden ? commonStyle.hidden : ''} ${batch.fakeHidden ? commonStyle.fakeHidden : ''}
          `}
          key={index}
          data-testid="SW-SR-Batch"
        >
          {batch.image && batch.image?.expand && (
            <div className={style.expandWrapper} data-testid="SW-Question-ExpandedImage">
              <ImageAsset
                asset={batch.image}
                className={style.bigImage}
                width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                size="medium"
                previewLabelTemplate={() => (
                  <TextWithOptions
                    text={batch.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                  />
                )}
              />
            </div>
          )}
          <div className={style.plainBlock} data-testid="SW-SR-Batch-Row">
            {batch.image && !batch.image?.expand && (
              <div className={style.answerImage}>
                <ImageAsset
                  position={localeOptions.rtl ? 'right' : 'left'}
                  asset={batch.image}
                  className={style.image}
                  width={imageAssetWidth}
                  height={36}
                  size="small"
                  previewLabelTemplate={() => (
                    <TextWithOptions
                      text={batch.text}
                      options={previewOptions}
                      rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                    />
                  )}
                />
              </div>
            )}
            {(!batch.image || batch.image.expand) && hasSmallImages ? (
              <ImageAsset width={imageAssetWidth} height={36} size="small" asset={null} />
            ) : null}

            <div className={style.answer} data-testid="SW-SR-Batch-Answer">
              {!batch.empty && (
                <div className={style.title} data-testid="SW-SR-Batch-Text">
                  <TextWithOptions
                    text={batch.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                  />
                </div>
              )}
              <div data-testid="SW-SR-Batch-Rating" className={style.ratingBlock}>
                <Stars
                  label={batch.text}
                  value={response.data[index]?.value}
                  size={schema.degree}
                  na={naMode}
                  index={index}
                  onChange={value => onChangeResponse(index, value)}
                  setFocused={setFocused}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </fieldset>
  )
}

export default StarRatingView
